import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger.js";

gsap.registerPlugin(ScrollTrigger);

function dm_vac_02_video_promo() {
	const sections = gsap.utils.toArray(".vac-02-video-promo");

	sections.forEach((section) => {
		const video = section.querySelector(".video-cont");
		const iframe = video?.querySelector("iframe");
		const content = section.querySelector(".container-fluid");
		const videoTrigger = section.querySelector(".video-trigger");
		const line1 = section.querySelector(".vac-02-video-promo__line--1");
		const line2 = section.querySelector(".vac-02-video-promo__line--2");
		const line3 = section.querySelector(".vac-02-video-promo__line--3");
		const isMobile = () => window.matchMedia("(max-width: 767.98px)").matches;

		if (!video || !iframe) return;

		let isPlaying = false;
		const playIcon = videoTrigger?.querySelector("[data-video-trigger-play]");
		const pauseIcon = videoTrigger?.querySelector("[data-video-trigger-pause]");

		const setVideoTriggerState = (playing) => {
			if (!videoTrigger) return;

			isPlaying = playing;
			const label = isPlaying ? "Pause video" : "Play video";

			videoTrigger.classList.toggle("is-playing", isPlaying);
			videoTrigger.setAttribute("aria-pressed", isPlaying ? "true" : "false");
			videoTrigger.setAttribute("aria-label", label);
			videoTrigger.setAttribute("title", label);

			if (playIcon) playIcon.hidden = false;
			if (pauseIcon) pauseIcon.hidden = true;
		};

		const postVimeoCommand = (method, value) => {
			iframe.contentWindow?.postMessage(
				value === undefined ? { method } : { method, value },
				"https://player.vimeo.com"
			);
		};

		const updateTriggerFromPlayer = (event) => {
			if (event.origin !== "https://player.vimeo.com" || event.source !== iframe.contentWindow) return;

			let data = event.data;
			if (typeof data === "string") {
				try {
					data = JSON.parse(data);
				} catch (error) {
					return;
				}
			}

			if (data?.event === "play") setVideoTriggerState(true);
			if (data?.event === "pause" || data?.event === "finish") setVideoTriggerState(false);
			if (data?.method === "getPaused" && typeof data.value === "boolean") {
				setVideoTriggerState(!data.value);
			}
		};

		const listenForPlayback = () => {
			postVimeoCommand("addEventListener", "play");
			postVimeoCommand("addEventListener", "pause");
			postVimeoCommand("addEventListener", "finish");
			postVimeoCommand("getPaused");
		};

		setVideoTriggerState(false);
		window.addEventListener("message", updateTriggerFromPlayer);
		iframe.addEventListener("load", listenForPlayback, { once: true });
		listenForPlayback();

		videoTrigger?.addEventListener("click", (event) => {
			event.preventDefault();
			const nextState = !isPlaying;
			postVimeoCommand(nextState ? "play" : "pause");
			setVideoTriggerState(nextState);
			videoTrigger.classList.toggle("is-dismissed", nextState);
		});

		videoTrigger?.addEventListener("pointerleave", () => {
			videoTrigger.classList.remove("is-dismissed");
		});

		video.addEventListener("pointerleave", () => {
			videoTrigger?.classList.remove("is-dismissed");
		});

		const createScrollNudge = (element, direction = 1, speed = 0.05) => {
			if (!element) return;
			const setX = gsap.quickSetter(element, "x", "px");

			ScrollTrigger.create({
				trigger: section,
				start: "top bottom",
				end: "bottom top",
				invalidateOnRefresh: true,
				onUpdate: (self) => {
					const delta = self.scroll() - self.start;
					setX(delta * direction * speed);
				},
				onRefresh: (self) => {
					const delta = self.scroll() - self.start;
					setX(delta * direction * speed);
				},
			});
		};

		createScrollNudge(line1, 1);
		createScrollNudge(line2, -1);
		createScrollNudge(line3, 1);

		const syncVideoAspectRatio = () => {
			const viewportWidth = Math.max(1, window.innerWidth);
			const viewportHeight = Math.max(1, window.innerHeight);
			const containerRatio = viewportWidth / viewportHeight;
			const vimeoRatio = 16 / 9;
			const iframeWidth = Math.max(1, vimeoRatio / containerRatio) * 100;
			const iframeHeight = Math.max(1, containerRatio / vimeoRatio) * 100;

			section.style.setProperty(
				"--video-promo-aspect-ratio",
				`${containerRatio}`
			);
			video.style.setProperty("--video-promo-iframe-width", `${iframeWidth}%`);
			video.style.setProperty("--video-promo-iframe-height", `${iframeHeight}%`);

			gsap.set(video, {
				clearProps: "transform",
			});
		};

		syncVideoAspectRatio();

		const syncMobileContentPosition = () => {
			if (!content) return;

			gsap.set(content, { clearProps: "transform" });
			if (!isMobile()) return;

			const videoRect = video.getBoundingClientRect();
			const sectionRect = section.getBoundingClientRect();
			const videoCenter = videoRect.top - sectionRect.top + (videoRect.height / 2);

			gsap.set(content, {
				y: (window.innerHeight / 2) - videoCenter,
			});
		};

		syncMobileContentPosition();

		let expandedVideo = { scaleX: 1, scaleY: 1, mobileScale: 1, x: 0, y: 0 };
		const updateExpandedVideo = () => {
			const viewportWidth = Math.max(1, window.innerWidth);
			const viewportHeight = Math.max(1, window.innerHeight);
			const videoRect = video.getBoundingClientRect();
			const sectionRect = section.getBoundingClientRect();

			expandedVideo = {
				scaleX: viewportWidth / Math.max(1, videoRect.width),
				scaleY: viewportHeight / Math.max(1, videoRect.height),
				mobileScale: Math.max(
					viewportWidth / Math.max(1, videoRect.width),
					viewportHeight / Math.max(1, videoRect.height)
				),
				x: -(videoRect.left - sectionRect.left),
				y: -(videoRect.top - sectionRect.top),
			};
		};

		updateExpandedVideo();

		let resizeRaf = null;
		window.addEventListener("resize", () => {
			if (resizeRaf) return;
			resizeRaf = requestAnimationFrame(() => {
				resizeRaf = null;
				syncVideoAspectRatio();
				ScrollTrigger.refresh();
			});
		});

		const timeline = gsap.timeline({
			scrollTrigger: {
				trigger: section,
				start: "top top",
				end: "+=100%",
				scrub: 0.5,
				pin: true,
				pinSpacing: true,
				markers: false,
				anticipatePin: 1,
				refreshPriority: 1,
				invalidateOnRefresh: true,
				onRefreshInit: () => {
					syncVideoAspectRatio();
					syncMobileContentPosition();
					updateExpandedVideo();
				},
			},
		});

		timeline.to(video, {
			scaleX: () => isMobile() ? expandedVideo.mobileScale : expandedVideo.scaleX,
			scaleY: () => isMobile() ? expandedVideo.mobileScale : expandedVideo.scaleY,
			x: () => isMobile() ? 0 : expandedVideo.x,
			y: () => isMobile() ? 0 : expandedVideo.y,
			transformOrigin: () => isMobile() ? "center center" : "top left",
			ease: "none",
			force3D: true,
		});
	});
}

export { dm_vac_02_video_promo };
